// The expert catalog: which profiles this app ships and how it colours them. Everything the user sees — name,
// tagline, description, badge, field — comes from the profile's own `style` block (schema 0.3), so there is no
// alias table to keep in step with profiles/. Profiles name a school of investing, never a person.
import catalog from './catalog.json';
import { profileDefinition } from './profiles';

export type Localized = Record<string, string>; // keyed by locale tag; 'en' always present

/** A published work the style is distilled from — the one place an author's name appears (schema 0.3 style.references). */
export interface Reference {
  work: string;
  author?: string;
  year?: number;
  note?: string;
}

export interface DomainMeta {
  id: string;
  name: Localized;
}

export interface ProfileMeta {
  id: string;
  color: string;
  domainId: string;
  sourceFile: string;
  version: string;
  name: Localized;
  tagline: Localized;
  description: Localized;
  badge: Localized;
  field: Localized;
  references: Reference[];
}

interface StyleBlock {
  name: Localized;
  tagline: Localized;
  description: Localized;
  badge: Localized;
  field: Localized;
  references?: Reference[];
}

export const DOMAINS: DomainMeta[] = catalog.domains;

export const PROFILES: ProfileMeta[] = catalog.profiles.map((p) => {
  const def = profileDefinition(p.id);
  const style = def.style as StyleBlock;
  return { id: p.id, color: p.color, domainId: p.domainId, sourceFile: p.sourceFile, version: String(def.version ?? ''), ...style, references: style.references ?? [] };
});

// Threads saved before 2026-09-24 name profiles by the person they were modelled on; map those ids forward.
const LEGACY: Record<string, string> = Object.fromEntries(catalog.profiles.flatMap((p) => (p.legacyIds ?? []).map((old) => [old, p.id])));

export function normalizeProfileId(id: string): string {
  return LEGACY[id] ?? id;
}

export function profileById(id: string): ProfileMeta | undefined {
  const wanted = normalizeProfileId(id);
  return PROFILES.find((p) => p.id === wanted);
}
